const MAX_POSTS = 12;

export function publicFeedURL(value, pageOrigin = typeof location === 'undefined' ? '' : location.origin) {
  try {
    const url = new URL(value);
    const localPreview = url.protocol === 'http:' && url.origin === pageOrigin && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    if ((!localPreview && url.protocol !== 'https:') || url.username || url.password || url.search || url.hash || url.pathname !== '/feed') return null;
    return url;
  } catch { return null; }
}

function instagramPostURL(value) {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || !['www.instagram.com', 'instagram.com'].includes(url.hostname) || url.username || url.password || url.port) return null;
    if (!/^\/(p|reel|tv)\/[A-Za-z0-9_-]+\/?$/.test(url.pathname)) return null;
    return `https://www.instagram.com${url.pathname}`;
  } catch { return null; }
}

function inlineVideoURL(value, endpoint, postId) {
  try {
    const url = new URL(value);
    const path = /^\/video\/(\d{1,32})-[a-f0-9]{16}$/.exec(url.pathname);
    if (url.origin !== endpoint.origin || url.username || url.password || url.search || url.hash || !path || path[1] !== postId) return null;
    return url.href;
  } catch { return null; }
}

export function normalizeFeed(data, endpoint, limit = MAX_POSTS, expectedProfile = 'merisi.works') {
  if (!endpoint || data?.version !== 1 || !Array.isArray(data.posts) ||
      typeof data.profile?.username !== 'string' || data.profile.username.toLowerCase() !== expectedProfile.toLowerCase()) return [];
  const seen = new Set();
  const posts = [];
  for (const item of data.posts.slice(0, 40)) {
    if (!item || typeof item.id !== 'string' || !/^[A-Za-z0-9_-]{1,160}$/.test(item.id) || seen.has(item.id)) continue;
    const permalink = instagramPostURL(item.permalink);
    let image;
    try { image = new URL(item.imageUrl); } catch { continue; }
    if (!permalink || image.origin !== endpoint.origin || image.username || image.password || image.search || image.hash || !/^\/media\/[A-Za-z0-9_-]{1,160}$/.test(image.pathname)) continue;
    const videoUrl = item.type === 'VIDEO' ? inlineVideoURL(item.videoUrl, endpoint, item.id) : null;
    seen.add(item.id);
    posts.push({
      id: item.id,
      permalink,
      imageUrl: image.href,
      type: ['IMAGE', 'VIDEO', 'CAROUSEL_ALBUM'].includes(item.type) ? item.type : 'IMAGE',
      alt: typeof item.alt === 'string' && item.alt.trim() ? item.alt.trim().slice(0, 300) : 'Instagram post from @merisi.works',
      ...(videoUrl ? { videoUrl } : {}),
    });
    if (posts.length >= Math.min(MAX_POSTS, Math.max(1, limit))) break;
  }
  return posts;
}

const ElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class MerisiInstagram extends ElementBase {
  connectedCallback() {
    if (this.controller) return;
    const generation = this.generation = (this.generation || 0) + 1;
    this.controller = new AbortController();
    this.viewport = this.querySelector('.merisi-ig__viewport');
    this.track = this.querySelector('.merisi-ig__track');
    this.previous = this.querySelector('.merisi-ig__previous');
    this.next = this.querySelector('.merisi-ig__next');
    this.status = this.querySelector('.merisi-ig__status');
    this.controls = this.querySelector('.merisi-ig__controls');
    this.position = this.querySelector('.merisi-ig__position');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.previous.addEventListener('click', () => this.move(-1), { signal: this.controller.signal });
    this.next.addEventListener('click', () => this.move(1), { signal: this.controller.signal });
    this.viewport.addEventListener('scroll', () => this.updateControls(), { signal: this.controller.signal, passive: true });
    this.viewport.addEventListener('keydown', (event) => this.onViewportKeydown(event), { signal: this.controller.signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseVideos();
    }, { signal: this.controller.signal });
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.viewport);
    this.videoObserver = new IntersectionObserver((entries) => this.onVideoVisibility(entries, generation), { threshold: 0.1 });
    const endpoint = publicFeedURL(this.dataset.feedUrl);
    if (!endpoint) { this.fallback(); return; }
    const intersectionObserver = new IntersectionObserver((entries) => {
      if (generation !== this.generation || !this.isConnected) return;
      if (!entries.some(entry => entry.isIntersecting)) return;
      intersectionObserver.disconnect();
      this.load(endpoint, generation);
    }, { rootMargin: '600px' });
    this.intersectionObserver = intersectionObserver;
    intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.pauseVideos();
    this.controller?.abort();
    this.requestController?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.videoObserver?.disconnect();
    this.controller = null;
  }

  async load(endpoint, generation = this.generation) {
    if (!this.isConnected || generation !== this.generation) return;
    const requestController = new AbortController();
    this.requestController = requestController;
    const timeout = setTimeout(() => requestController.abort(), 8000);
    const current = () => this.isConnected && generation === this.generation && this.requestController === requestController;
    try {
      const response = await fetch(endpoint.href, { credentials: 'omit', referrerPolicy: 'no-referrer', signal: requestController.signal });
      if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) throw new Error('Unavailable feed');
      const text = await response.text();
      if (text.length > 100000) throw new Error('Oversized feed');
      const posts = normalizeFeed(JSON.parse(text), endpoint, Number(this.dataset.maxPosts) || MAX_POSTS, this.dataset.profile || 'merisi.works');
      if (!current()) return;
      if (!posts.length) { this.fallback(); return; }
      this.pauseVideos();
      this.videoObserver?.disconnect();
      this.track.replaceChildren(...posts.map(post => this.card(post)));
      this.dataset.state = 'ready';
      this.viewport.hidden = false;
      this.controls.hidden = false;
      this.updateControls();
    } catch {
      if (current()) this.fallback();
    } finally {
      clearTimeout(timeout);
    }
  }

  card(post) {
    if (post.type === 'VIDEO' && post.videoUrl) return this.videoCard(post);
    const item = document.createElement('li');
    item.className = 'merisi-ig__card';
    const link = document.createElement('a');
    link.className = 'merisi-ig__post';
    link.href = post.permalink;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.setAttribute('aria-label', post.type === 'VIDEO' ? `Open video on Instagram: ${post.alt} (opens in a new tab)` : `${post.alt} (opens on Instagram)`);
    const img = document.createElement('img');
    img.src = post.imageUrl;
    img.alt = post.alt;
    img.width = 600;
    img.height = 600;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.addEventListener('error', () => {
      item.remove();
      if (!this.track.children.length) this.fallback();
      else this.updateControls();
    }, { once: true, signal: this.controller.signal });
    link.append(img);
    if (post.type === 'VIDEO') {
      const cue = document.createElement('span');
      cue.className = 'merisi-ig__video-cue';
      cue.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.className = 'merisi-ig__video-label';
      label.textContent = 'Open on Instagram';
      cue.append(label);
      link.append(cue);
    }
    if (post.type !== 'IMAGE') {
      const badge = document.createElement('span');
      badge.className = 'merisi-ig__badge';
      badge.textContent = post.type === 'VIDEO' ? 'Video' : 'Photos';
      link.append(badge);
    }
    item.append(link);
    return item;
  }

  videoCard(post) {
    const generation = this.generation;
    const controller = this.controller;
    const current = () => this.isConnected && generation === this.generation && this.controller === controller && !controller.signal.aborted;
    const item = document.createElement('li');
    item.className = 'merisi-ig__card';
    const frame = document.createElement('div');
    frame.className = 'merisi-ig__post merisi-ig__video';
    const video = document.createElement('video');
    video.poster = post.imageUrl;
    video.preload = 'none';
    video.playsInline = true;
    video.autoplay = false;
    video.controls = false;
    video.crossOrigin = 'anonymous';
    video.setAttribute('aria-label', `Video: ${post.alt}`);
    const play = document.createElement('button');
    play.type = 'button';
    play.className = 'merisi-ig__video-play';
    play.setAttribute('aria-label', `Play video: ${post.alt}`);
    const symbol = document.createElement('span');
    symbol.className = 'merisi-ig__play-symbol';
    symbol.setAttribute('aria-hidden', 'true');
    symbol.textContent = '▶';
    const label = document.createElement('span');
    label.className = 'merisi-ig__video-label';
    label.textContent = 'Play video';
    play.append(symbol, label);
    const status = document.createElement('p');
    status.className = 'merisi-ig__video-status';
    status.setAttribute('role', 'status');
    status.hidden = true;
    const link = document.createElement('a');
    link.className = 'merisi-ig__video-link';
    link.href = post.permalink;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.setAttribute('aria-label', `Open video on Instagram: ${post.alt} (opens in a new tab)`);
    link.textContent = 'Open on Instagram';
    const arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = ' ↗';
    link.append(arrow);
    let failed = false;
    const showPlay = () => {
      if (failed || !current()) return;
      play.hidden = false;
      label.textContent = video.ended ? 'Replay video' : 'Play video';
      play.setAttribute('aria-label', `${video.ended ? 'Replay' : 'Play'} video: ${post.alt}`);
    };
    const unavailable = () => {
      if (!current()) return;
      failed = true;
      video.pause();
      video.controls = false;
      play.hidden = true;
      status.textContent = 'Video unavailable here. Open it on Instagram.';
      status.hidden = false;
    };
    play.addEventListener('click', async () => {
      if (!current() || failed) return;
      this.pauseVideos(video);
      if (!video.getAttribute('src')) video.src = post.videoUrl;
      video.controls = true;
      video.tabIndex = 0;
      play.hidden = true;
      status.hidden = true;
      if (video.ended) video.currentTime = 0;
      try {
        const started = video.play();
        video.focus({ preventScroll: true });
        await started;
      } catch (error) {
        if (!current()) return;
        if (error?.name === 'AbortError') showPlay();
        else unavailable();
      }
    }, { signal: controller.signal });
    video.addEventListener('play', () => {
      if (!current()) { video.pause(); return; }
      this.pauseVideos(video);
      play.hidden = true;
    }, { signal: controller.signal });
    video.addEventListener('pause', showPlay, { signal: controller.signal });
    video.addEventListener('ended', showPlay, { signal: controller.signal });
    video.addEventListener('error', unavailable, { signal: controller.signal });
    frame.append(video, play, status);
    item.append(frame, link);
    this.videoObserver?.observe(video);
    return item;
  }

  pauseVideos(except) {
    for (const video of this.track?.querySelectorAll?.('video') || []) {
      if (video !== except) video.pause();
    }
  }

  onVideoVisibility(entries, generation = this.generation) {
    if (!this.isConnected || generation !== this.generation) return;
    for (const entry of entries) {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.1) entry.target.pause();
    }
  }

  onViewportKeydown(event) {
    if (event.target !== this.viewport || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Home' || event.key === 'End') {
      this.pauseVideos();
      this.viewport.scrollTo({ left: event.key === 'Home' ? 0 : this.viewport.scrollWidth, behavior: 'auto' });
    } else this.move(event.key === 'ArrowRight' ? 1 : -1);
  }

  move(direction) {
    this.pauseVideos();
    const item = this.track.firstElementChild;
    if (!item) return;
    const step = item.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).gap) || 0);
    const visible = Math.max(1, Math.round(this.viewport.clientWidth / step));
    this.viewport.scrollBy({ left: direction * step * visible, behavior: this.motion.matches ? 'auto' : 'smooth' });
  }

  updateControls() {
    if (!this.viewport || this.viewport.hidden || !this.track.children.length) return;
    const max = this.viewport.scrollWidth - this.viewport.clientWidth;
    this.previous.disabled = this.viewport.scrollLeft <= 2;
    this.next.disabled = this.viewport.scrollLeft >= max - 2;
    const step = this.track.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).gap) || 0);
    const first = Math.round(this.viewport.scrollLeft / step) + 1;
    const visible = Math.max(1, Math.round(this.viewport.clientWidth / step));
    const label = `${first}–${Math.min(this.track.children.length, first + visible - 1)} / ${this.track.children.length}`;
    if (this.position.textContent !== label) this.position.textContent = label;
  }

  fallback() {
    this.pauseVideos();
    this.videoObserver?.disconnect();
    this.dataset.state = 'unavailable';
    this.viewport.hidden = true;
    this.controls.hidden = true;
    this.track.replaceChildren();
    this.status.textContent = 'See the latest posts on Instagram.';
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('merisi-instagram')) customElements.define('merisi-instagram', MerisiInstagram);
